import prepareAlgoliaSwatchImages from '../../../utils/prepareAlgoliaSwatchImages';
import Icon from '../../Atoms/Icon/Icon';
import DocumentsIcons from '../../../utils/documentsIcons';
import prepareAlgoliaChipImages from '../../../utils/prepareAlgoliaChipImages';
import { useAnalyticsPushEvent } from '../../../analytic/analytic.context';
import ProductAttributeGroup from '../ProductAttributeGroup';
import { useLocation } from '../../../context/location.context';

const generateGlobalSearchAutocompleteChildren = ({
  suggestions,
  products,
  documents,
  searchTerm,
  defaultIndex,
}) => {
  const pushAnalyticEvent = useAnalyticsPushEvent();
  const { regionIds } = useLocation();
  if (
    !suggestions?.hits?.length &&
    !products?.hits?.length &&
    !documents?.hits?.length
  ) {
    return [];
  }

  const renderSuggestions = () => {
    if (!suggestions?.nbHits) {
      return [];
    }

    const suggestionsDataList = [];

    suggestionsDataList.push({
      id: 'suggestions-title',
      value: 'Search suggestions',
      heading: true,
    });

    const suggestionsItemsList = suggestions.hits.map((item) => {
      const itemName =
        // eslint-disable-next-line no-underscore-dangle
        item._highlightResult?.query?.value ||
        item.media_field_name ||
        item.media_name;

      const clearName = item.query;
      return {
        value: (
          <div className="global-search-dynamic__suggestion">
            <div
              className="global-search-dynamic__suggestion-title"
              dangerouslySetInnerHTML={{ __html: itemName }}
            />{' '}
            {item[defaultIndex]?.facets?.exact_matches?.sgna_bundle_label
              ?.length > 0 &&
            item[defaultIndex]?.facets?.exact_matches?.sgna_bundle_label[0]
              ?.value ? (
              <div className="global-search-dynamic__suggestion-in">
                in{' '}
                {
                  item[defaultIndex].facets.exact_matches.sgna_bundle_label[0]
                    .value
                }
              </div>
            ) : null}
          </div>
        ),
        searchValue: clearName,
      };
    });

    suggestionsDataList.push(...suggestionsItemsList);

    return suggestionsDataList;
  };

  const handleAnalyticsClick = (e) => {
    const link = e.target;

    if (link.closest('.global-search-header')) {
      pushAnalyticEvent({
        event: 'search_result_clicked',
        event_origin: 'front_end',
        search_term: searchTerm.queryValue,
        link_url: link.href,
        link_text: link.textContent,
        content_type: 'search_auto_complete',
      });
    }
  };

  const renderProducts = () => {
    if (!products?.nbHits) {
      return [];
    }

    const productsDataList = [];

    productsDataList.push({
      value: (
        <div className="global-search-dynamic__products-title">Products</div>
      ),
      heading: true,
    });

    const productsItemsList = products.hits.map((item) => {
      const swatchImagesList = item.field_is_specialty_ceiling
        ? prepareAlgoliaChipImages(item.product_color_chips)
        : prepareAlgoliaSwatchImages(item.product_color_swatches, regionIds);

      let imageObject = {};

      if (item.field_image) {
        imageObject = JSON.parse(item.field_image);
      }

      const itemHTML = (
        <div className="global-search-dynamic__product">
          {!!imageObject.external_url && (
            <img
              className="global-search-dynamic__product-img"
              src={imageObject.external_url}
              alt={imageObject.alt}
            />
          )}
          <div className="global-search-dynamic__product-content">
            <ul className="global-search-dynamic__product-tags">
              {item?.field_product_category && (
                <li>{item?.field_product_category}</li>
              )}
              {item?.product_new_tag && <li>new</li>}
            </ul>
            <a
              className="global-search-dynamic__product-title"
              href={item.url}
              onClick={handleAnalyticsClick}
            >
              {item.title}
            </a>
            {swatchImagesList?.length > 0 && (
              <ProductAttributeGroup
                items={swatchImagesList}
                keyProp={`product-card`}
                type={'image'}
                enableCustomColor={true}
                enableMoreButtonClick={false}
                machineName={`global-search-autocomplete-swatches-${item.nid}`}
                customFixedItemsLength={4}
              />
            )}
          </div>
        </div>
      );

      return {
        id: item.nid,
        value: itemHTML,
        isLink: true,
      };
    });

    productsDataList.push(...productsItemsList);

    return productsDataList;
  };

  const renderDocuments = () => {
    if (!documents?.nbHits) {
      return [];
    }

    const documentsDataList = [];

    documentsDataList.push({
      id: 'documents-title',
      value: 'Documents',
      heading: true,
    });

    const documentsItemsList = documents.hits.map((item) => {
      const documentTitle =
        item.media_field_name?.replace(/<\/?[^>]+(>|$)/g, '') ||
        item.media_name;

      const itemHTML = (
        <div className="global-search-dynamic__document">
          <div className="global-search-dynamic__document-header">
            <Icon
              iconName={
                DocumentsIcons[item?.second_eyebrow?.toLowerCase()] ||
                DocumentsIcons.default
              }
            />
            <ul className="global-search-dynamic__document-tags">
              {item.second_eyebrow && <li>{item.second_eyebrow}</li>}
              {item.media_field_region_top_abbreviation && (
                <li>{item.media_field_region_top_abbreviation}</li>
              )}
              {item.media_field_language_abbreviation && (
                <li>{item.media_field_language_abbreviation}</li>
              )}
            </ul>
          </div>
          <a
            className="global-search-dynamic__document-link"
            href={item.sgna_media_download_url}
            onClick={handleAnalyticsClick}
          >
            {documentTitle}
          </a>
        </div>
      );

      return {
        id: item.mid,
        value: itemHTML,
        isLink: true,
        modClass: 'document',
      };
    });

    documentsDataList.push(...documentsItemsList);

    return documentsDataList;
  };

  const renderSeeAllButton = () => {
    const searchValue =
      searchTerm?.globalSearch || searchTerm?.queryValue || '';

    if (!searchValue) {
      return [];
    }

    const linkMarkup = (
      <div className="global-search-dynamic__see-all-link-wrap">
        <a
          className="global-search-dynamic__see-all-link"
          href={`/search?query=${searchValue}`}
        >
          {Drupal.t('See All')}
        </a>
      </div>
    );

    return [
      {
        id: 'see-all-link',
        value: linkMarkup,
        isLink: true,
      },
    ];
  };

  return [
    ...renderSuggestions(),
    ...renderProducts(),
    ...renderDocuments(),
    ...renderSeeAllButton(),
  ];
};

export default generateGlobalSearchAutocompleteChildren;
